import React, { Fragment } from "react"
import Switch from "react-switch"
import Col from "react-bootstrap/lib/Col"
import debounce from "lodash/debounce"
import StickyBox from "react-sticky-box"

import * as VenuesAPI from "../apis/VenuesAPI"
import { diff_in_days } from "../utils/dates/diff_in_days"
import { setToLocalStorage, getFromLocalStorage } from "../utils/localStorage"
import BrowserLocationHelper from "../utils/browser_location_helper"
import sendAddressToIntercom from "../utils/Intercom/sendAddressToIntercom"
import EmptyVenuesAlert from "./components/EmptyVenuesAlert/EmptyVenuesAlert"
import CampaignsBannerHorizontalList from "./components/CampaignsBanner/CampaignsBannerHorizontalList"
import VenueChooserSearchBar from "./components/filters/VenueChooserSearchBar"
import VenueCategoriesChooser from "./components/filters/VenueCategoriesChooser"
import VenueFeaturesChooser from "./components/filters/VenueFeaturesChooser"
import VenuesFilterModal from "./components/filters/VenuesFilterModal/index.jsx"
import LoadingVenues from "./components/LoadingVenues"
import ErrorMessage from "./components/ErrorMessage"
import ServicesList from "./ServicesList"
import VenuesList from "./VenuesList"
import ZoneNotCovered from "./ZoneNotCovered/"
import { ORDER_MODE } from "./OrderMode"

import {
  gtmSetUserProperties,
  logVenueClicked,
  logServiceClicked,
} from "../apis/Analytics/gtmActions"
import { GetSearchedServices } from "../apis/VenuesAPI"

const storejs = require("store")

import style from "./style.module.sass"

const FILTERING_EXPIRATION = "filtering_expiration"
const FILTERING_CATEGORIES = "filtering_categories"
const FILTERING_FEATURES = "filtering_features"

function getVenueURL(url, citySlug, venueSlug) {
  return url.replace(":city", citySlug).replace(":restaurant", venueSlug)
}

function getInitialSelectedFeatures() {
  return {
    creditCard: false,
    discount: false,
    cash: false,
    new: false,
    promotion: false,
  }
}

function getInitialCategories(categories) {
  return categories
    .map((category) => {
      return {
        id: category.id,
        name: category.name,
        is_root: category.is_root,
        selected: false,
      }
    })
    .filter((category) => !category.is_root)
}

export default class VenueChooserApp extends React.Component {
  constructor(props) {
    super(props)

    // check and reset stored filtering
    let now = new Date()
    let expiration = storejs.get(FILTERING_EXPIRATION)
    if (now > expiration) {
      storejs.set(FILTERING_CATEGORIES, null)
      storejs.set(FILTERING_FEATURES, null)
    }
    storejs.set(FILTERING_EXPIRATION, now.setMinutes(now.getMinutes() + 30))

    this.state = {
      errorMessage: null,
      categories: [],
      showFilterModal: false,
      resultsCount: 0,
      venues: [],
      isSearching: false,
      isLoading: false,
      searchFilteredVenues: [],
      searchFilteredServices: [],
      loaded: false,
      takeAwaySelected: false,
      selectedFeatures: {},
    }
  }

  componentDidMount() {
    this.loadVenues()
  }

  loadVenues = () => {
    let queryParam = {}

    if (JSON.parse(getFromLocalStorage("take_away")) === true) {
      this.setState({
        takeAwaySelected: true,
      })
      queryParam = { take_away: true }
    }

    VenuesAPI.getVenues(this.props.location, queryParam)
      .then((data) => {
        if (data.delivery_zone) {
          gtmSetUserProperties({ delivery_zone: data.delivery_zone.name })

          sendAddressToIntercom(this.props.location, data.delivery_zone.name)
        }

        let categories = getInitialCategories(data.venue_categories)
        let storedCategories = storejs.get(FILTERING_CATEGORIES) || []
        categories.forEach((category) => {
          if (
            storedCategories.some((stored) => {
              return category.id === stored.id && stored.selected
            })
          ) {
            category.selected = true
          }
        })
        storejs.set(FILTERING_CATEGORIES, categories)

        let features = getInitialSelectedFeatures()
        let storedFeatures = storejs.get(FILTERING_FEATURES) || {}
        for (let key in features) {
          features[key] = features[key] || storedFeatures[key]
        }
        storejs.set(FILTERING_FEATURES, features)

        this.setState({
          loaded: true,
          zoneNotCovered: !data.delivery_zone && data.venues.length === 0,
          venues: data.venues,
          categories: categories,
          selectedFeatures: features,
        })
      })
      .catch((err) => {
        // TODO: missing handling errors
        this.setState({
          errorMessage: I18n.t("pages.locations.venues.generic_loading_error"),
        })
      })
  }

  toggleTakeAwayModeWithLocalStorage = (takeawayMode) => {
    this.setState({
      takeAwaySelected: takeawayMode,
    })
    setToLocalStorage("take_away", takeawayMode)
  }

  handleOnAllCategoriesSelected = () => {
    let newCategories = getInitialCategories(this.state.categories)
    storejs.set(FILTERING_CATEGORIES, newCategories)
    this.setState({
      categories: newCategories,
    })
  }

  handleOnCategorySelected = (id) => {
    let categories = this.state.categories

    for (let category of categories) {
      if (category.id === id) {
        category.selected = !category.selected
        break
      }
    }

    storejs.set(FILTERING_CATEGORIES, categories)
    this.setState({
      categories: categories,
    })
  }

  handleVenueClick = (event, venue) => {
    event.stopPropagation()

    const { slug, name, street_name, city, delivery_zone } = venue
    const { showVenueURL } = this.props
    const citySlug = delivery_zone ? delivery_zone.slug : city

    const url = getVenueURL(showVenueURL, citySlug, slug)

    logVenueClicked(`${name} - ${street_name}, ${city}`)

    if (event.shiftKey || event.ctrlKey || event.metaKey) {
      window.open(url, "_blank")
    } else {
      BrowserLocationHelper.ChangeURL(url)
    }
  }

  handleServiceClick = (venue, service) => {
    const { slug, name, street_name, city, delivery_zone } = venue
    const { showVenueURL } = this.props
    const citySlug = delivery_zone ? delivery_zone.slug : city

    const url = getVenueURL(showVenueURL, citySlug, slug)

    logServiceClicked(
      `${service?.name || "no_name"} - ${name} - ${street_name}, ${city}`
    )

    BrowserLocationHelper.ChangeURL(
      url +
        `?service_id=${service.id}&service_section_id=${
          service.section?.id || ""
        }`
    )
  }

  handleShowFilterModal = () => {
    this.setState({ showFilterModal: true })
  }

  handleOnCloseFilterModal = () => {
    this.setState({ showFilterModal: false })
  }

  handleOnClearFilterClicked = () => {
    storejs.set(FILTERING_CATEGORIES, null)
    storejs.set(FILTERING_FEATURES, null)

    this.setState({
      showFilterModal: false,
      selectedFeatures: getInitialSelectedFeatures(),
      categories: getInitialCategories(this.state.categories),
    })
  }

  handleTakeawayChange = () => {
    this.setState({ takeAwayChangeLoading: true }, () => {
      const queryParam = !this.state.takeAwaySelected ? { take_away: true } : {}

      VenuesAPI.getVenues(this.props.location, queryParam)
        .then((data) => {
          this.setState({
            takeAwayChangeLoading: false,
            loaded: true,
            zoneNotCovered: false,
            venues: data.venues,
            categories: getInitialCategories(data.venue_categories),
          })
          this.toggleTakeAwayModeWithLocalStorage(!this.state.takeAwaySelected)
        })
        .catch((err) => {
          // TODO: handle all error cases (i.e. err is undefined)
          if (err && err.indexOf && err.indexOf("zone-not-covered") !== -1) {
            this.setState({
              takeAwayChangeLoading: false,
              loaded: true,
              venues: [],
              categories: [],
              zoneNotCovered: true,
            })
          } else {
            this.setState({ takeAwayChangeLoading: false })
          }
        })
    })
  }

  handleOnFeatureSelected = (name) => {
    const selectedFeatures = this.state.selectedFeatures
    selectedFeatures[name] = !selectedFeatures[name]

    storejs.set(FILTERING_FEATURES, selectedFeatures)
    this.setState({ selectedFeatures })
  }

  getVenueCategories() {
    return this.state.categories
  }

  getVisibleVenues() {
    const selectedCategoriesIds = this.getSelectedVenueCategories().map(
      (category) => category.id
    )

    let venues = this.state.isSearching
      ? this.state.searchFilteredVenues
      : this.state.venues

    // Filter venues that does not accept discount codes
    if (this.state.selectedFeatures["discount"]) {
      venues = venues.filter((venue) => venue.accepts_discount_codes === true)
    }

    // Filter venues that does not accept credit cards
    if (this.state.selectedFeatures["creditCard"]) {
      venues = venues.filter((venue) => venue.accepts_stripe === true)
    }

    // Filter venues that does not accept cash
    if (this.state.selectedFeatures["cash"]) {
      venues = venues.filter((venue) => venue.accepts_cash === true)
    }

    // Filter venues that are new
    if (this.state.selectedFeatures["new"]) {
      venues = venues.filter(
        (venue) => -diff_in_days(venue.first_published_at) < 30
      )
    }

    // Filter venues that have promotions
    if (this.state.selectedFeatures["promotion"]) {
      venues = venues.filter((venue) => !!venue.promotion)
    }

    // Filter by selected categories
    if (selectedCategoriesIds.length === 0) {
      return venues
    } else {
      return venues.filter((venue) => {
        return venue.venue_category_ids.some(
          (category_id) => selectedCategoriesIds.indexOf(category_id) !== -1
        )
      })
    }
  }

  handleSearchOnVenues = (text) => {
    // filter venues and venues' services based on searchbar text
    if (!!text.trim()) {
      let filtered = this.state.venues.filter((el) => {
        return (el?.name || "").toLowerCase().includes(text.toLowerCase())
      })
      this.setState({ isSearching: true, searchFilteredVenues: filtered })
      this.handleSearchOnServices(text)
    } else {
      this.setState({
        isLoading: false,
        isSearching: false,
        searchFilteredServices: [],
      })
    }
  }

  handleSearchOnServices = (text) => {
    let venueIds = this.state.venues.map((v) => v.id)
    this.setState({ isLoading: true })
    GetSearchedServices(text, venueIds)
      .then((res) => {
        this.setState({
          isLoading: false,
          searchFilteredServices: res?.dishes || [],
        })
      })
      .catch((err) => {
        this.setState({ isLoading: false })
      })
  }

  getSelectedVenueCategories() {
    return this.getVenueCategories().filter(
      (category) => category.selected === true
    )
  }

  getAppliedFiltersCount() {
    let count = this.getSelectedVenueCategories().length

    count += Object.values(this.state.selectedFeatures).filter(
      (value) => value === true
    ).length

    return count
  }

  getOrderMode() {
    return this.state.takeAwaySelected ? ORDER_MODE.PICKUP : ORDER_MODE.DELIVERY
  }

  renderSearchBar() {
    const handler = debounce(this.handleSearchOnVenues, 1000)

    return (
      <Fragment>
        <VenueChooserSearchBar
          onChange={handler}
          isLoading={this.state.isLoading}
        />
      </Fragment>
    )
  }

  render() {
    if (this.state.errorMessage) {
      return <ErrorMessage message={this.state.errorMessage} />
    }

    if (!this.state.loaded) {
      return <LoadingVenues />
    }

    const venues = this.getVisibleVenues()
    const { zoneNotCovered } = this.state
    const { location } = this.props
    const appliedFiltersCount = this.getAppliedFiltersCount()
    const venuesCount = venues.length

    return (
      <div className="search-content">
        <div className="visible-xs visible-sm">{this.renderSearchBar()}</div>
        <div className="layout-container">
          <StickyBox
            offsetTop={101}
            className="visible-md visible-lg col-md-4 in-page-filters"
          >
            <div className={style.filteringContainer}>
              {this.renderSearchBar()}
              <div className="hidden-xs hidden-sm">
                <div className={style.filterSection}>
                  {I18n.t("pages.locations.venues.features.title")}
                </div>
                <VenueFeaturesChooser
                  selectedFeatures={this.state.selectedFeatures}
                  onFeatureSelected={this.handleOnFeatureSelected}
                />
                <hr />
                <div className={style.filterSection} style={{ marginTop: 40 }}>
                  {I18n.t("pages.locations.venues.cuisines.title")}
                </div>
                <VenueCategoriesChooser
                  categories={this.state.categories}
                  onSelect={this.handleOnCategorySelected}
                  onAllCategoriesSelected={this.handleOnAllCategoriesSelected}
                />
              </div>
            </div>
          </StickyBox>

          <div className="search-results">
            {/* TODO: handle TLT specific campaign banners  */}
            {/* <div className="row p-4 pt-0">
              <CampaignsBannerHorizontalList
                userToken={this.props.userToken}
                url={this.props.campaignsURL}
              />
            </div> */}

            <div className="row p-4 pt-0">
              {venuesCount > 0 && (
                <div className="in-page-filters__results-count pl-4">
                  <strong>{venuesCount}</strong>{" "}
                  {venuesCount > 1
                    ? I18n.t("pages.locations.venues.plural")
                    : I18n.t("pages.locations.venues.singular")}
                </div>
              )}

              {appliedFiltersCount > 0 && (
                <div
                  onClick={this.handleOnClearFilterClicked}
                  className="in-page-filters__remove pr-4"
                >
                  <i className="fal fa-close mr-2" />
                  {I18n.t("pages.locations.venues.features.remove_filters") +
                    " "}
                  <strong>({appliedFiltersCount})</strong>
                </div>
              )}
            </div>

            {zoneNotCovered && <ZoneNotCovered location={location} />}
            {!zoneNotCovered &&
            venuesCount === 0 &&
            !this.state.isLoading &&
            this.state.searchFilteredServices.length === 0 ? (
              <EmptyVenuesAlert />
            ) : (
              <div className={style.resultContainer}>
                <VenuesList
                  orderMode={this.getOrderMode()}
                  venues={venues}
                  onClick={this.handleVenueClick}
                />
                <ServicesList
                  orderMode={this.getOrderMode()}
                  isLoading={this.state.isLoading}
                  venues={this.state.venues}
                  services={this.state.searchFilteredServices}
                  onClick={this.handleServiceClick}
                />
              </div>
            )}
          </div>
        </div>

        <div className="hidden-lg hidden-md">
          <div
            className="btn btn-orange btn-lg venues-filters-circle-button"
            onClick={this.handleShowFilterModal}
          >
            <i className="fal fa-sliders-h mr-2" />
            <span className="value">{appliedFiltersCount}</span>
          </div>
          <VenuesFilterModal
            show={this.state.showFilterModal}
            categories={this.state.categories}
            onCategorySelected={this.handleOnCategorySelected}
            onAllCategoriesSelected={this.handleOnAllCategoriesSelected}
            onClose={this.handleOnCloseFilterModal}
            selectedFeatures={this.state.selectedFeatures}
            onFeatureSelected={this.handleOnFeatureSelected}
            onClearFilterClicked={this.handleOnClearFilterClicked}
            resultsCount={venues.length}
          />
        </div>
      </div>
    )
  }
}
